// photo_sync.js — progress photos travel between devices as encrypted blobs.
//
// The photo records ({ id, createdAt, date, pose }) sync with the rest of the
// log (sync.js collection 'photos'). The images are uploaded separately, after
// a successful sync, to /api/photos/:vaultId: each one is encrypted on the
// device with AES-256-GCM under a key derived from the vault key, split into
// 512 KB chunks and committed. Its server name (blobId) is an HMAC of the photo
// id, so the server can't tell which photo is which. Other devices download a
// photo the first time it is shown (fetchPhoto), and keep it locally.
//
// The local image store is injected (photos.jsx wires IndexedDB; tests use memory):
//   configurePhotoStore({ has(id) → bool, get(id) → { bytes, mime } | null, put(id, { bytes, mime }) })

import { getSyncCode, keyBytesFromCode, deriveKeys, syncApi, onSyncComplete } from './sync.js';

export const PHOTO_CHUNK = 512 * 1024;
const te = new TextEncoder();
const td = new TextDecoder();
const subtle = () => globalThis.crypto.subtle;
const hex = (buf) => Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
function photoError(code, message, extra = {}) { const e = new Error(message); e.code = code; Object.assign(e, extra); return e; }

let store = null;
export function configurePhotoStore(s) { store = s; }

// ───────────────────────────── keys + format ─────────────────────────────

let kc = null;
/** Keys for the current vault: { code, vaultId, key (AES-GCM), mac (HMAC for blob ids) } or null. */
export async function photoKeys(code = getSyncCode()) {
  if (!code) return null;
  if (kc && kc.code === code) return kc;
  const raw = keyBytesFromCode(code);
  if (!raw) return null;
  const { vaultId } = await deriveKeys(code);
  const ikm = await subtle().importKey('raw', raw, 'HKDF', false, ['deriveKey', 'deriveBits']);
  const salt = te.encode('liftlog/photos/v1');
  const key = await subtle().deriveKey({ name: 'HKDF', hash: 'SHA-256', salt, info: te.encode('aes-256-gcm') }, ikm, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const macBits = await subtle().deriveBits({ name: 'HKDF', hash: 'SHA-256', salt, info: te.encode('blob-id') }, ikm, 256);
  const mac = await subtle().importKey('raw', macBits, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  kc = { code, vaultId, key, mac, ids: new Map() };
  return kc;
}
/** Server name of a photo: 32 hex chars, unlinkable to the photo id without the vault key. */
export async function blobIdFor(keys, photoId) {
  const id = String(photoId);
  let b = keys.ids.get(id);
  if (!b) { b = hex(await subtle().sign('HMAC', keys.mac, te.encode('photo|' + id))).slice(0, 32); keys.ids.set(id, b); }
  return b;
}

/** image bytes → 'L' 'P' 1 | iv(12) | AES-GCM( u32 header length | header JSON | bytes ) */
export async function encryptPhoto(bytes, mime, keys, blobId) {
  const head = te.encode(JSON.stringify({ v: 1, mime: mime || 'image/webp' }));
  const plain = new Uint8Array(4 + head.length + bytes.length);
  new DataView(plain.buffer).setUint32(0, head.length);
  plain.set(head, 4); plain.set(bytes, 4 + head.length);
  const iv = new Uint8Array(12);
  globalThis.crypto.getRandomValues(iv);
  const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: te.encode('liftlog/photo/v1|' + blobId) }, keys.key, plain));
  const out = new Uint8Array(3 + 12 + ct.length);
  out.set([0x4c, 0x50, 1]); out.set(iv, 3); out.set(ct, 15);
  return out;
}
export async function decryptPhoto(buf, keys, blobId) {
  const b = new Uint8Array(buf);
  if (b.length < 15 + 16 || b[0] !== 0x4c || b[1] !== 0x50 || b[2] !== 1) throw photoError('format', 'Not a Lift Log photo');
  let plain;
  try {
    plain = new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv: b.slice(3, 15), additionalData: te.encode('liftlog/photo/v1|' + blobId) }, keys.key, b.slice(15)));
  } catch (_) { throw photoError('decrypt', 'The photo couldn’t be unlocked'); }
  const n = new DataView(plain.buffer, plain.byteOffset).getUint32(0);
  const head = JSON.parse(td.decode(plain.subarray(4, 4 + n)));
  return { bytes: plain.slice(4 + n), mime: head.mime || 'image/webp' };
}

// ───────────────────────────── status ─────────────────────────────

let status = { state: 'idle', used: null, quota: null, count: null, pending: 0, error: null, at: null };
const listeners = new Set();
function emit(patch) {
  status = { ...status, ...patch };
  for (const fn of listeners) { try { fn(status); } catch (_) { /* ignore */ } }
}
export function photoSyncStatus() { return status; }
export function subscribePhotoSync(fn) { listeners.add(fn); fn(status); return () => listeners.delete(fn); }

// ───────────────────────────── transport ─────────────────────────────

async function req(path, init) {
  let res;
  try { res = await syncApi(path, { cache: 'no-store', ...init }); } catch (_) { throw photoError('offline', 'Can’t reach the sync server'); }
  return res;
}
async function jsonOf(res) { try { return await res.json(); } catch (_) { return {}; } }

let listCache = null; // { vaultId, at, map: Map(blobId → { n, bytes }) }
async function listRemote(keys) {
  const res = await req(`/photos/${keys.vaultId}`);
  if (res.status === 404 || res.status === 410) return null;
  if (!res.ok) throw photoError('http', `Photo sync failed (HTTP ${res.status})`);
  const j = await jsonOf(res);
  if (j.vault === false) return null;
  const map = new Map((j.blobs || []).map((b) => [b.id, b]));
  listCache = { vaultId: keys.vaultId, at: Date.now(), map };
  emit({ used: j.used ?? null, quota: j.quota ?? null, count: map.size });
  return { map, used: j.used, quota: j.quota };
}

/** Encrypt + upload one photo (chunks, then commit). → bytes uploaded */
export async function uploadPhoto(keys, photoId, bytes, mime) {
  const blobId = await blobIdFor(keys, photoId);
  const enc = await encryptPhoto(bytes, mime, keys, blobId);
  const n = Math.max(1, Math.ceil(enc.length / PHOTO_CHUNK));
  for (let i = 0; i < n; i++) {
    const res = await req(`/photos/${keys.vaultId}/${blobId}/${i}`, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: enc.subarray(i * PHOTO_CHUNK, (i + 1) * PHOTO_CHUNK) });
    if (res.status === 507) { const j = await jsonOf(res); throw photoError(j.error === 'photo-quota' ? 'quota' : 'full', j.error === 'photo-quota' ? 'Photo storage is full' : 'The server is out of space', { used: j.used, quota: j.quota }); }
    if (res.status === 429) throw photoError('rate', 'Uploading too fast — will continue shortly');
    if (!res.ok) throw photoError('http', `Photo upload failed (HTTP ${res.status})`);
  }
  const res = await req(`/photos/${keys.vaultId}/${blobId}/commit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ n }) });
  if (!res.ok) throw photoError('http', `Photo upload failed (HTTP ${res.status})`);
  const j = await jsonOf(res);
  if (listCache && listCache.vaultId === keys.vaultId) listCache.map.set(blobId, { id: blobId, n, bytes: enc.length });
  emit({ used: j.used ?? status.used, quota: j.quota ?? status.quota });
  return enc.length;
}

/** Download + decrypt one photo. → { bytes, mime } | null when it isn't on the server (yet). */
export async function downloadPhoto(keys, photoId) {
  const blobId = await blobIdFor(keys, photoId);
  let entry = listCache && listCache.vaultId === keys.vaultId && Date.now() - listCache.at < 60000 ? listCache.map.get(blobId) : undefined;
  if (entry === undefined) {
    const res = await req(`/photos/${keys.vaultId}/${blobId}`);
    if (res.status === 404 || res.status === 410) return null;
    if (!res.ok) throw photoError('http', `Photo download failed (HTTP ${res.status})`);
    entry = await jsonOf(res);
  }
  if (!entry || !entry.n) return null; // not uploaded (yet)
  const parts = [];
  let size = 0;
  for (let i = 0; i < entry.n; i++) {
    const res = await req(`/photos/${keys.vaultId}/${blobId}/${i}`);
    if (res.status === 404) return null;
    if (!res.ok) throw photoError('http', `Photo download failed (HTTP ${res.status})`);
    const b = new Uint8Array(await res.arrayBuffer());
    parts.push(b); size += b.length;
  }
  const all = new Uint8Array(size);
  let o = 0; for (const p of parts) { all.set(p, o); o += p.length; }
  return decryptPhoto(all, keys, blobId);
}

// ───────────────────────────── engine ─────────────────────────────

let running = null;
let rerun = null;
/**
 * Upload this device's photos the server doesn't have yet, and delete the
 * server copies of photos deleted on any device. Runs after every sync.
 */
export function syncPhotos(state) {
  if (running) { rerun = state; return running; }
  running = (async () => {
    try { await runPhotoSync(state); } finally {
      running = null;
      if (rerun) { const s = rerun; rerun = null; setTimeout(() => syncPhotos(s), 0); }
    }
    return status;
  })();
  return running;
}
async function runPhotoSync(state) {
  const keys = await photoKeys();
  if (!keys || !store || !state) return;
  const live = (Array.isArray(state.photos) ? state.photos : []).filter((p) => p && p.id != null);
  const tomb = Object.keys((state.deleted && state.deleted.photos) || {});
  try {
    const remote = await listRemote(keys);
    if (!remote) return; // the vault isn't on the server yet (first sync still running)
    const liveIds = new Set(live.map((p) => String(p.id)));
    for (const id of tomb) {
      if (liveIds.has(id)) continue;
      const b = await blobIdFor(keys, id);
      if (!remote.map.has(b)) continue;
      const res = await req(`/photos/${keys.vaultId}/${b}`, { method: 'DELETE' });
      if (res.ok) { remote.map.delete(b); const j = await jsonOf(res); emit({ used: j.used ?? status.used, count: remote.map.size }); }
    }
    const todo = [];
    for (const p of live) {
      const b = await blobIdFor(keys, p.id);
      if (!remote.map.has(b) && (await store.has(p.id))) todo.push(p);
    }
    emit({ state: todo.length ? 'uploading' : 'idle', pending: todo.length, error: null });
    for (let i = 0; i < todo.length; i++) {
      const data = await store.get(todo[i].id);
      if (data && data.bytes && data.bytes.length) await uploadPhoto(keys, todo[i].id, data.bytes, data.mime);
      emit({ pending: todo.length - i - 1, count: listCache ? listCache.map.size : status.count });
    }
    emit({ state: 'idle', pending: 0, error: null, at: Date.now() });
  } catch (e) {
    emit({ state: e.code === 'quota' || e.code === 'full' ? 'quota' : e.code === 'offline' ? 'offline' : 'error', error: e.message, ...(e.quota ? { used: e.used, quota: e.quota } : {}) });
  }
}

// Lazy download, at most 3 at a time; one request per photo id in flight.
const inflight = new Map();
let active = 0;
const waiting = [];
const slot = () => (active < 3 ? (active++, Promise.resolve()) : new Promise((r) => waiting.push(r)));
const release = () => { const n = waiting.shift(); if (n) n(); else active--; };
/**
 * A photo this device doesn't have yet: download it from the synced copy and
 * keep it locally. → { bytes, mime } | null (not synced yet, or sync is off).
 */
export function fetchPhoto(photoId) {
  const id = String(photoId);
  if (inflight.has(id)) return inflight.get(id);
  const p = (async () => {
    const keys = await photoKeys().catch(() => null);
    if (!keys) return null;
    await slot();
    try {
      const got = await downloadPhoto(keys, id);
      if (got && store) await store.put(id, got);
      return got;
    } catch (_) { return null; } finally { release(); }
  })();
  inflight.set(id, p);
  p.finally(() => inflight.delete(id));
  return p;
}

onSyncComplete((state) => syncPhotos(state));
